import { CountryThreshold, IRoute } from '../../core/types';
import Routes from '../../components/Routes';
import Page404 from '../Page404/Page404';
import { useAppSelector } from '../../hooks';
import SellerDashboard from './SellerDashboard';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PublicRoutesEnum, SellerRoutesEnum } from '../../core/enums';
import SellerDataFiles from './SellerDataFiles';
import { IntegrationsProvider } from '../../context/integrations-context';
import SellerLayout from './SellerLayout';
import SellerVATIds from './SellerVATIds/SellerVATIds';
import SellerSetupCheck from './SellerSetupCheck';
import { useQuery } from 'react-query';
import { getClientThresholdByCountries } from '../../core/api';
import { useEffect, useMemo } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useHistory } from 'react-router-dom';
import SellerCountryDetails from './SellerCountryDetails/SellerCountryDetails';
import Inventory from './Inventory/Inventory';
import SellerServices from './SellerServices/SellerServices';
import SellerSales from './SellerSales/SellerSales';
import InvoiceBook from '../TaxAdvisor/InvoiceBook/InvoiceBook';
import SellerRecommendations from './SellerRecommendations/SellerRecommendations';
import SellerAmazonFees from './SellerAmazonFees/SellerAmazonFees';
import Unsubscribe from './Unsubscribe/Unsubscribe';
import SellerFiling from './SellerFiling/SellerFiling';
import PurchasePrices from '../PurchasePrices/PurchasePrices';
import { PPProvider } from '../../context/pp-context';
import { ThresholdProvider } from '../../context/threshold-context';
import { MUProvider } from '../../context/mu-context';

const SellerRoutes = () => {
  const activeCompany = useAppSelector((s) => s.user.activeCompany);
  const user = useAppSelector((s) => s.user.user);

  const history = useHistory();
  const { vs2912CpPrototypeV2 } = useFlags();
  const params = new URLSearchParams(history.location.search);
  const unsubscribe = params.get('unsubscribe');

  const { data: thresholdByCountries, isLoading } = useQuery(
    ['SellerThresholdByCountry', user!.Id, activeCompany?.company_id],
    () => getClientThresholdByCountries(activeCompany?.company_id)
  );

  const threshold = useMemo(
    () =>
      Math.round(
        (thresholdByCountries || []).reduce(
          (acc: number, item: CountryThreshold) => {
            return acc + item.threshold.value;
          },
          0
        )
      ),
    [thresholdByCountries]
  );

  const location = history.location;
  useEffect(() => {
    if (
      !isLoading &&
      threshold === 0 &&
      location.pathname === SellerRoutesEnum.SetupOverview
    ) {
      history.push('/');
    }
  }, [isLoading, threshold, location, history]);

  useEffect(() => {
    if (
      activeCompany &&
      !activeCompany.ManualUploadNeeded &&
      location.pathname === SellerRoutesEnum.DataFiles
    ) {
      history.push('/');
    }
  }, [activeCompany, history, location.pathname]);

  const routes: IRoute[] = [
    {
      component: unsubscribe ? Unsubscribe : SellerDashboard,
      path: '/',
      exact: true,
    },
    ...(activeCompany?.ManualUploadNeeded
      ? [
          {
            component: SellerDataFiles,
            path: SellerRoutesEnum.DataFiles,
          },
        ]
      : []),
    {
      component: SellerVATIds,
      path: SellerRoutesEnum.VAT,
    },
    {
      component: PurchasePrices,
      path: SellerRoutesEnum.PurchasePrices,
    },
    ...[
      threshold > 0 && !isLoading
        ? {
            component: SellerSetupCheck,
            path: SellerRoutesEnum.SetupOverview,
          }
        : {
            path: SellerRoutesEnum.SetupOverview,
            redirectTo: '/',
          },
    ],
    ...(vs2912CpPrototypeV2
      ? [
          {
            component: SellerCountryDetails,
            path: SellerRoutesEnum.CountryDetails,
          },
          {
            component: Inventory,
            path: SellerRoutesEnum.Inventory,
          },
          {
            component: InvoiceBook,
            path: SellerRoutesEnum.InvoiceBook,
          },
          {
            component: SellerServices,
            path: SellerRoutesEnum.Services,
          },
          {
            component: SellerSales,
            path: SellerRoutesEnum.Sales,
          },
          {
            component: SellerRecommendations,
            path: SellerRoutesEnum.Recommendations,
          },
          {
            component: SellerAmazonFees,
            path: SellerRoutesEnum.AmazonFees,
          },
        ]
      : []),
    {
      component: SellerFiling,
      path: SellerRoutesEnum.Filing,
    },
    {
      component: Page404,
      path: PublicRoutesEnum.Page404,
    },
    {
      path: '*',
      redirectTo: PublicRoutesEnum.Page404,
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ThresholdProvider
        threshold={threshold}
        thresholdByCountries={thresholdByCountries}
      >
        <IntegrationsProvider>
          <PPProvider>
            <MUProvider>
              <SellerLayout>
                <Routes routes={routes} />
              </SellerLayout>
            </MUProvider>
          </PPProvider>
        </IntegrationsProvider>
      </ThresholdProvider>
    </>
  );
};

export default SellerRoutes;
